import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    useTheme,
    Dialog,
    DialogContent,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import Toggle from '../../components/toggle';
import InputField from '../../components/formItem/inputField';
import { SortableHeaderCell, HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { createSortConfig, handleSort, formatDateTimeDisplay, isAdmin, isSupervisor, toDisplayString, getLocalizedName } from '../../utils/base';
import { championAPI, membershipAPI } from '../../actions/imsServer';
import { CHAMPIONSHIP_TYPES, API_PAGE_SIZE } from '../../constants';
import { IcSearch, Prev, Next } from '../../assets/images';
import { ChampionshipBackground } from '../../assets/images/championshipBackground';
import ChampionshipDetailContent from '../../components/championship/ChampionshipDetailContent';
import BoxData from '../../components/boxData';

const ChampionshipAdmin = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const organizationId = location.state?.organizationId;

    const theme = useTheme();

    const [championships, setChampionships] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [activeSearchText, setActiveSearchText] = useState(''); // 실제 필터링에 사용되는 검색어
    const [progressFilter, setProgressFilter] = useState(1); // 0: 완료, 1: 진행중
    const [sortConfig, setSortConfig] = useState(createSortConfig('startDate', 'desc'));
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0); // 서버에서 받은 전체 개수
    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // meInfo에서 슈퍼바이저 여부 확인
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
    const [, setUserOrganizationIds] = useState([]); // 사용자의 기관 ID 목록

    // 팝업 상태
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedChampionshipData, setSelectedChampionshipData] = useState(null);

    const fetchData = async (page = 1, currentProgressFilter = progressFilter, currentFilterType = filterType, currentSearchText = activeSearchText, currentSortConfig = sortConfig) => {
        try {
            setLoading(true);
            setError(null);

            const currentOffset = (page - 1) * ITEMS_PER_PAGE;

            // admin이면 사용자의 기관 목록 가져오기
            let userOrgId = null;
            if (isAdmin(meInfo?.grade) && meInfo?.id) {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    userOrgId = memberships.length > 0 ? memberships[0].organizationId : null;

                    if (userOrgId) {
                        setUserOrganizationIds([userOrgId]);
                    } else {
                        // 어드민이지만 기관이 없으면 빈 배열로 설정 (기관 ID 0인 데이터만 보여주기 위해)
                        setUserOrganizationIds([]);
                    }
                } catch (err) {
                    console.error('Failed to fetch user organizations:', err);
                    // 에러 발생 시에도 빈 배열로 설정
                    setUserOrganizationIds([]);
                }
            }

            // progressFilter: 0 = complete (state=2), 1 = ongoing (state=0)
            const stateValue = currentProgressFilter === 0 ? '2' : '0';

            // organizationId 파라미터 설정 (복수개 지원)
            const championParams = {
                state: stateValue,
                expand: 'organization,user,content',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE
            };

            if (organizationId) {
                // 단일 organizationId가 전달된 경우
                championParams.organizationId = organizationId;
            } else if (isAdmin(meInfo?.grade)) {
                // 어드민인 경우: userOrgId를 사용하여 설정
                if (userOrgId) {
                    // 사용자의 기관이 있는 경우: 사용자의 기관 ID와 0 포함
                    championParams.organizationId = `${userOrgId},0`;
                } else {
                    // 사용자의 기관이 없는 경우: 0만
                    championParams.organizationId = '0';
                }
            }

            // 서버사이드 정렬 파라미터 추가
            if (currentSortConfig.key) {
                championParams.sort = currentSortConfig.key;
                championParams.order = currentSortConfig.direction === 'asc' ? 'asc' : 'desc';
            }

            // 서버사이드 필터링 파라미터 추가
            switch (currentFilterType) {
                case 'supervisor_championship':
                    championParams.organizationId = '0';
                    break;
                case 'championship_search_by_org':
                    if (currentSearchText.trim()) {
                        championParams.organizationKeyword = currentSearchText.trim();
                    }
                    break;
                case 'championship_search_by_name':
                    if (currentSearchText.trim()) {
                        championParams.keyword = currentSearchText.trim();
                    }
                    break;
                case 'all':
                default:
                    break;
            }


            const championsRes = await championAPI.getChampions(championParams);
            const champions = championsRes?.data?.champions || championsRes?.champions || [];
            const totalCount = championsRes?.data?.total || championsRes?.total || 0;
            setTotal(totalCount);

            // champions에서 organization 정보 추출 (expand로 포함되어 있음)
            const organizationMap = new Map();
            champions.forEach(c => {
                if (c.organization && c.organization.id) {
                    organizationMap.set(c.organization.id, c.organization);
                }
            });
            const fetchedOrganizations = Array.from(organizationMap.values());

            // admin이면 사용자의 기관만 필터링
            let filteredOrganizations = fetchedOrganizations;
            if (isAdmin(meInfo?.grade) && userOrgId) {
                filteredOrganizations = fetchedOrganizations.filter(org => {
                    const orgId = String(org.id);
                    return String(userOrgId) === orgId;
                });
            }
            setOrganizations(filteredOrganizations);

            const rows = champions.map(c => {
                // expand: 'organization,user,content'로 받은 content 정보 사용
                const content = c.content || {};
                // state 기준으로 진행중/완료 구분 (내부 키: complete / ongoing)
                // state: 0 → 진행중, state: 2 → 완료
                const state = c.state !== undefined ? c.state : 0;
                const progress = state === 2 ? 'complete' : 'ongoing';

                // 대회주최자 이름 결정
                // userGrade가 슈퍼바이저(80 이상)이면 '디딤 본사'
                // userGrade가 어드민(50-60)이면 organization.name
                // userGrade가 유저(그 외)이면 userName
                let hostName = c?.userName || '-';
                const userGrade = c?.userGrade || 0;

                if (isSupervisor(userGrade)) {
                    hostName = t('didim_hq');
                } else if (isAdmin(userGrade)) {
                    // 어드민인 경우 챔피언십의 organizationId로 기관 이름 가져오기
                    hostName = c.organization?.name || '-';
                }

                return {
                    id: c.id,
                    startDate: c.startDate,
                    endDate: c.endDate,
                    organizationId: c.organizationId,
                    organizationName: c.organization?.name || '-',
                    name: c.name || '-',
                    contentName: getLocalizedName(content, i18n.language) || '-',
                    contentIndex: c.contentIndex,
                    icon: content?.icon || null,
                    userId: c.userId,
                    userName: hostName,
                    userGrade: c?.userGrade || 0,
                    type: c.type,
                    password: c.password || '',
                    playerCount: typeof c.playerCount === 'number' ? c.playerCount : 0,
                    progress,
                    state: state,
                };
            });

            setChampionships(rows);
        } catch (err) {
            console.error('Failed to fetch championships:', err);
            setError(t('failed_to_load_championship_list'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        setChampionships([]);
        fetchData(1, progressFilter, filterType, activeSearchText, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 의존성에서 제외
    }, [organizationId, progressFilter, filterType, activeSearchText, sortConfig]);

    // 어드민인 경우 'championship_search_by_org' 필터가 선택되어 있으면 'all'로 변경
    useEffect(() => {
        if (isAdmin(meInfo?.grade) && filterType === 'championship_search_by_org') {
            setFilterType('all');
            setActiveSearchText('');
        }
    }, [meInfo?.grade, filterType]);

    // 페이지 변경 시 데이터 다시 가져오기
    useEffect(() => {
        if (currentPage > 1) {
            fetchData(currentPage, progressFilter, filterType, activeSearchText, sortConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 의존성에서 제외
    }, [currentPage, progressFilter, filterType, activeSearchText, sortConfig]);

    // organizationId가 있고 organizations가 로드되면 필터 설정
    useEffect(() => {
        if (organizationId && organizations.length > 0) {
            const targetOrg = organizations.find(org =>
                org.id === organizationId || String(org.id) === String(organizationId)
            );
            if (targetOrg && targetOrg.name) {
                setFilterType('championship_search_by_org');
                setSearchText(targetOrg.name);
                setActiveSearchText(targetOrg.name);
            }
        }
    }, [organizationId, organizations]);

    // 검색 실행 함수
    const handleSearch = () => {
        setActiveSearchText(searchText);
        // 검색 시 첫 페이지로 리셋
        setCurrentPage(1);
        // 검색 실행 시 데이터 다시 가져오기
        fetchData(1, progressFilter, filterType, searchText, sortConfig);
    };

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(total / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
    }, [total]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(total / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, total, handlePageChange]);

    const onSort = (key) => {
        const next = handleSort(sortConfig, key);
        setSortConfig(next);
        // 정렬 시 첫 페이지로 리셋
        setCurrentPage(1);
        // 정렬 변경 시 데이터 다시 가져오기
        fetchData(1, progressFilter, filterType, activeSearchText, next);
    };

    const handleCreate = () => {
        navigate('/championships/admin-add');
    };

    const handleRowClick = (championship) => {
        // ChampionshipDetailContent에 필요한 형태로 데이터 변환
        const championshipData = {
            id: championship.id,
            name: championship.name,
            type: championship.type,
            startDate: championship.startDate,
            endDate: championship.endDate,
            contentName: championship.contentName,
            contentIndex: championship.contentIndex,
            icon: championship.icon,
            playerCount: championship.playerCount,
            organizationId: championship.organizationId,
            userName: championship.userName,
            userGrade: championship.userGrade,
        };
        setSelectedChampionshipData(championshipData);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedChampionshipData(null);
    };

    const handleForceEnd = async () => {
        if (!selectedChampionshipData?.id) return;

        try {
            await championAPI.updateChampion({
                id: selectedChampionshipData.id,
                state: 2
            });

            // 강제종료 후 데이터 다시 로드
            await fetchData(currentPage);

            // 팝업 닫기
            handleCloseDetailDialog();
        } catch (error) {
            console.error('강제종료 실패:', error);
            alert(t('force_end_failed'));
        }
    };

    return (
        <ContentArea>
            {/* 검색/필터 영역 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 2.5, py: 2.5, mb: 3, gap: 2 }}>
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Dropdown
                            options={[
                                { value: 'all', label: 'all' },
                                { value: 'supervisor_championship', label: 'supervisor_championship' },
                                ...(isAdmin(meInfo?.grade) ? [] : [{ value: 'championship_search_by_org', label: 'search_by_organization' }]),
                                { value: 'championship_search_by_name', label: 'search_by_championship_name' },
                            ]}
                            value={filterType}
                            onChange={(option) => {
                                setFilterType(option.value);
                                setSearchText('');
                                setActiveSearchText('');
                                setCurrentPage(1);
                                fetchData(1, progressFilter, option.value, '', sortConfig);
                            }}
                            placeholder={t('select_filter')}
                        />
                        {(filterType === 'championship_search_by_org' || filterType === 'championship_search_by_name') && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                <InputField
                                    type="text"
                                    placeholder={
                                        filterType === 'championship_search_by_org' ? t('enter_championship_organization_name') :
                                            t('enter_championship_name')
                                    }
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                    onBlur={() => {
                                        // 포커스 아웃 시 검색 실행
                                        if (searchText !== activeSearchText) {
                                            handleSearch();
                                        }
                                    }}
                                    showClearIcon={true}
                                    onClear={() => {
                                        setSearchText('');
                                        setActiveSearchText('');
                                    }}
                                    sx={{ flex: 1 }}
                                />
                                <IconButton
                                    onClick={handleSearch}
                                    sx={{
                                        width: '24px',
                                        height: '24px',
                                        p: 0,
                                        color: theme.palette.figma.iconWhite,
                                        '&:hover': {
                                            backgroundColor: theme.palette.figma.white[20],
                                        }
                                    }}
                                >
                                    <IcSearch color={theme.palette.figma.iconWhite} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    <Toggle
                        options={[t('complete'), t('in_progress')]}
                        value={progressFilter}
                        onChange={(index) => {
                            setProgressFilter(index);
                            // 진행 상태 필터 변경 시 첫 페이지로 리셋하고 데이터 재로드
                            setCurrentPage(1);
                            setChampionships([]);
                            fetchData(1, index, filterType, activeSearchText, sortConfig);
                        }}
                        sx={{ width: 'auto', minWidth: '200px' }}
                    />
                </Box>

            </Box>

            {/* 에러 */}
            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* 테이블 */}
            {!loading && (
                <Box sx={{ px: 2.5, mb: 3 }}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <SortableHeaderCell columnKey="startDate" label={t('championship_start_date')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="endDate" label={t('championship_end_date')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="organizationId" label={t('championship_organization')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="name" label={t('championship_name')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="contentIndex" label={t('championship_content')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="userName" label={t('championship_host')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="type" label={t('championship_format')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="password" label={t('public_private')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="playerCount" label={t('tournament_participants')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <HeaderCell label={t('progress')} align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(championships) && championships.length > 0 ? championships.map((c, idx) => (
                                    <TableRow
                                        key={c.id || idx}
                                        onClick={() => handleRowClick(c)}
                                        sx={{
                                            ...getTableStyles(theme).tableRowStyle,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: theme.palette.figma.white[5],
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Typography sx={{ fontSize: '12px' }}>{formatDateTimeDisplay(c.startDate, 'date')}</Typography>
                                            <Typography sx={{ fontSize: '12px' }}>{formatDateTimeDisplay(c.startDate, 'time')}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '12px' }}>{formatDateTimeDisplay(c.endDate, 'date')}</Typography>
                                            <Typography sx={{ fontSize: '12px' }}>{formatDateTimeDisplay(c.endDate, 'time')}</Typography>
                                        </TableCell>
                                        <TableCell>{c.organizationName}</TableCell>
                                        <TableCell>{c.name}</TableCell>
                                        <TableCell>{c.contentName}</TableCell>
                                        <TableCell>{c.userName}</TableCell>
                                        <TableCell>{(() => { const typ = CHAMPIONSHIP_TYPES.find(type => type.value === c.type); return typ ? toDisplayString(t(typ.label), typ.label) : '-'; })()}</TableCell>
                                        <TableCell>{c.password ? t('private') : t('public')}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{c.playerCount}</TableCell>
                                        <TableCell>{c.progress === 'complete' ? t('complete') : c.progress === 'ongoing' ? t('in_progress') : c.progress}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                                            {loading ? t('loading') : t('no_championship_data')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {total > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)',
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(total / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(total / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(total / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* 하단 생성 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                <Button onClick={handleCreate}>{isAdmin(meInfo?.grade) ? t('create_organization_championship') : t('create_supervisor_championship')}</Button>
            </Box>

            {/* 챔피언십 상세 팝업 */}
            <Dialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                slotProps={{
                    root: { keepMounted: false },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG || theme.palette.figma.block,
                        width: '360px',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '600px',
                    }}
                >
                    {/* Background */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'stretch',
                        zIndex: 0
                    }}>
                        <ChampionshipBackground sx={{ width: '100%', height: '100%' }} />
                    </Box>

                    {/* 상단 오른쪽: 사용자 이름과 닫기 버튼 */}
                    <Box sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        {selectedChampionshipData?.userName && (
                            <BoxData
                                data={selectedChampionshipData.userName + (isAdmin(selectedChampionshipData?.userGrade) ? `\n(${t('administrator')})` : '')}
                                variant="default"
                                sx={{
                                    height: 'auto',
                                    minHeight: '22px',
                                    '& .MuiTypography-root': {
                                        whiteSpace: 'pre-line',
                                        textAlign: 'center',
                                    }
                                }}
                            />
                        )}
                    </Box>

                    {/* 챔피언십 상세 컴포넌트 */}
                    {selectedChampionshipData && (
                        <Box sx={{
                            width: '100%',
                            flex: 1,
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            zIndex: 1,
                            paddingTop: '10px',
                        }}>
                            <ChampionshipDetailContent
                                championshipData={selectedChampionshipData}
                            />
                        </Box>
                    )}

                    {/* 하단 버튼 영역 - 좌측 닫기, 우측 강제종료 */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px',
                        flexShrink: 0,
                        borderTop: `1px solid ${theme.palette.figma.white[10]}`,
                        position: 'relative',
                        zIndex: 2,
                        gap: 2,
                    }}>
                        <Button onClick={handleCloseDetailDialog}>
                            {t('close')}
                        </Button>
                        <Button onClick={handleForceEnd}>
                            {t('force_end')}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </ContentArea>
    );
};

export { ChampionshipAdmin };


